"use client";

import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import apiClient from "@/lib/api";
import { notifyProfileChanged } from "@/lib/profileRevalidation";
import {
  getGuestCareerStage,
  clearGuestCareerStage,
  type CareerStageValue,
} from "@/lib/guestCareerStage";

const OPTIONS: Array<{
  value: CareerStageValue;
  title: string;
  description: string;
}> = [
  {
    value: "new_grad",
    title: "就活（新卒・第二新卒）",
    description: "これから社会人になる方・第二新卒の方向けの質問で練習します",
  },
  {
    value: "mid_career",
    title: "転職（社会人経験あり）",
    description: "すでに働いた経験がある方向けの質問で練習します",
  },
];

type Status = "idle" | "checking" | "resolved" | "blocked";

// ログイン済でプロフィールの練習タイプ（career_stage）が未設定の場合に、全画面共通で
// ブロックダイアログを表示し、就活/転職を選ぶまで他操作をブロックする。
// 未ログイン・確認中・設定済みのときは何も描画しない。
export default function CareerStageGuard() {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<Status>("idle");
  const [selected, setSelected] = useState<CareerStageValue | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function patchCareerStage(stage: CareerStageValue) {
    await apiClient.patch("/users/me", { profile: { career_stage: stage } });
    // ヘッダーの練習タイプバッジなど、取得済みの値を出している画面に取り直させる
    notifyProfileChanged();
  }

  useEffect(() => {
    if (authLoading || !user) {
      setStatus("idle");
      return;
    }

    let cancelled = false;
    setStatus("checking");

    (async () => {
      try {
        const res = await apiClient.get("/users/me");
        if (cancelled) return;

        const careerStage = res.data?.profile?.career_stage ?? null;
        if (careerStage) {
          setStatus("resolved");
          return;
        }

        // 未設定でも未ログイン時に選んだ区分が残っていれば引き継いで解除する
        // （auth.tsx の引き継ぎとフェッチが競合した場合の保険）。
        const pending = getGuestCareerStage();
        if (pending) {
          try {
            await patchCareerStage(pending);
            clearGuestCareerStage();
            if (!cancelled) setStatus("resolved");
            return;
          } catch {
            // 引き継ぎ失敗時は通常のブロックダイアログに倒す
          }
        }

        if (!cancelled) setStatus("blocked");
      } catch {
        // 取得失敗時は操作をロックしない（他画面の通常表示を優先する）
        if (!cancelled) setStatus("idle");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await patchCareerStage(selected);
      clearGuestCareerStage();
      setStatus("resolved");
    } catch {
      setError("保存に失敗しました。もう一度お試しください。");
    } finally {
      setSaving(false);
    }
  };

  if (status !== "blocked") return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="career-stage-guard-title"
    >
      <div className="bg-white rounded-2xl w-full max-w-md p-6 slide-up">
        <h2
          id="career-stage-guard-title"
          className="text-lg font-bold mb-2 text-center"
        >
          まずは練習タイプを選びましょう
        </h2>
        <p className="text-sm text-center mb-3 leading-relaxed text-balance">
          あなたの状況に合わせて、質問とフィードバックの内容が変わります。
        </p>
        {/* 変更方向は就活→転職の一方向のみ許可されるため、選択中の練習タイプで注意文を分ける。
            切り替え不可・履歴非表示は事実として残しつつ、警告色ではなく補足として見せる。 */}
        <p className="text-xs text-center mb-6 leading-relaxed text-on-surface-variant">
          {selected === "mid_career"
            ? "「転職」を選ぶと、あとから「就活」には切り替えられません。"
            : "迷ったら、いまの立場に近いほうを選んでください。あとから「転職」に切り替えられます。切り替えると、それまでの練習履歴は見られなくなります。"}
        </p>

        <div
          className="space-y-3 mb-6"
          role="radiogroup"
          aria-label="練習タイプの選択"
        >
          {OPTIONS.map((opt) => {
            const isSelected = selected === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => setSelected(opt.value)}
                className={`w-full text-left p-4 rounded-xl border bg-white transition-all min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  isSelected
                    ? "border-2 border-primary"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <span
                  className={`block text-base font-bold ${
                    isSelected ? "text-primary" : "text-on-surface"
                  }`}
                >
                  {opt.title}
                </span>
                <span className="block text-xs mt-1 leading-relaxed">
                  {opt.description}
                </span>
              </button>
            );
          })}
        </div>

        {error && (
          <p className="text-error text-sm text-center mb-3">{error}</p>
        )}

        <button
          type="button"
          onClick={handleConfirm}
          disabled={!selected || saving}
          className="w-full bg-gradient-to-r from-primary to-accent text-white font-bold text-sm rounded-lg min-h-[48px] px-6 transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? "保存中..." : "この内容で始める"}
        </button>
      </div>
    </div>
  );
}
